import Check from "@mui/icons-material/Check";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Refresh from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { ReactNode, useState } from "react";
import { encampDarkTheme } from "theme";
import { OAK_MONOSPACE_FONT_FAMILY } from "util/fontStacks";

const ENV_PLACEHOLDER = "<sandbox>";

const MOONLIGHT = "#f3e7bf";

// A few fixed stars over a dusk-to-midnight gradient; the page is dark by design.
const NIGHT_SKY = [
  "radial-gradient(1.5px 1.5px at 12% 18%, #fff, transparent)",
  "radial-gradient(1px 1px at 27% 62%, #fff, transparent)",
  "radial-gradient(1.5px 1.5px at 41% 9%, #fff, transparent)",
  "radial-gradient(1px 1px at 58% 78%, #fff, transparent)",
  "radial-gradient(2px 2px at 69% 24%, #fff, transparent)",
  "radial-gradient(1px 1px at 83% 52%, #fff, transparent)",
  "radial-gradient(1.5px 1.5px at 91% 12%, #fff, transparent)",
  "radial-gradient(1px 1px at 6% 84%, #fff, transparent)",
  "radial-gradient(1px 1px at 36% 92%, #fff, transparent)",
  "radial-gradient(1px 1px at 77% 90%, #fff, transparent)",
  "radial-gradient(2px 2px at 18% 40%, #fff, transparent)",
  "radial-gradient(1.5px 1.5px at 52% 30%, #fff, transparent)",
  "radial-gradient(2px 2px at 95% 70%, #fff, transparent)",
  "linear-gradient(180deg, #050816 0%, #0b1430 55%, #1a2550 100%)",
].join(", ");

function CrescentMoon() {
  return (
    <Box
      component="svg"
      viewBox="0 0 64 64"
      aria-hidden
      sx={{
        width: { xs: 56, sm: 72 },
        height: { xs: 56, sm: 72 },
        flexShrink: 0,
        filter: `drop-shadow(0 0 18px ${MOONLIGHT}66)`,
      }}
    >
      <defs>
        <mask id="sandbox-off-crescent">
          <rect width="64" height="64" fill="white" />
          <circle cx="44" cy="22" r="22" fill="black" />
        </mask>
      </defs>
      <circle cx="30" cy="32" r="24" fill={MOONLIGHT} mask="url(#sandbox-off-crescent)" />
    </Box>
  );
}

function Code({ children }: { children: ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        fontFamily: OAK_MONOSPACE_FONT_FAMILY,
        fontSize: "0.9em",
        px: 0.5,
        borderRadius: 0.5,
        bgcolor: "action.hover",
      }}
    >
      {children}
    </Box>
  );
}

function CommandBlock({ command }: { command: string }) {
  const copyToClipboard = useCopyToClipboard();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await copyToClipboard(command);
    setCopied(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        my: 1.5,
        pl: 2,
        pr: 1,
        py: 1,
        borderRadius: 1,
        bgcolor: "rgba(0, 0, 0, 0.4)",
        border: 1,
        borderColor: "divider",
        color: "common.white",
      }}
    >
      <Box
        component="pre"
        sx={{
          m: 0,
          flex: 1,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          fontFamily: OAK_MONOSPACE_FONT_FAMILY,
          fontSize: "0.95rem",
        }}
      >
        {command}
      </Box>
      <Tooltip title={copied ? "Copied" : "Copy command"}>
        <IconButton aria-label="Copy command" onClick={handleCopy} sx={{ color: "inherit" }}>
          {copied ? <Check /> : <ContentCopy />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function errorDetails(error: unknown): string {
  if (!(error instanceof Error)) return String(error);
  const networkError: unknown = Reflect.get(error, "networkError");
  const statusCode: unknown =
    networkError instanceof Error ? Reflect.get(networkError, "statusCode") : undefined;
  return [
    `${error.name}: ${error.message}`,
    networkError instanceof Error && `Network error: ${networkError.name}: ${networkError.message}`,
    typeof statusCode === "number" && `HTTP status: ${statusCode}`,
    `API: ${import.meta.env.VITE_API_URL || "(VITE_API_URL not set)"}`,
    error.stack,
  ]
    .filter(Boolean)
    .join("\n");
}

type SandboxOffPageProps = {
  /** The sandbox's environment name, e.g. `pickletown`. */
  env?: string;
  /** The API error that led here, shown under "Show error details". */
  error?: unknown;
  /** Defaults to reloading the page, which re-runs the failed API call. */
  onCheckAgain?: () => void;
};

/** Shown instead of the error page when a personal sandbox's API is scaled down for the night. */
export function SandboxOffPage({
  env,
  error,
  onCheckAgain = () => window.location.reload(),
}: SandboxOffPageProps) {
  const [checking, setChecking] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const profile = env ?? ENV_PLACEHOLDER;

  const checkAgain = () => {
    setChecking(true);
    onCheckAgain();
  };

  return (
    <ThemeProvider theme={encampDarkTheme}>
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: NIGHT_SKY,
          color: "text.primary",
          px: 2,
          py: 6,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 760,
            p: { xs: 3, sm: 5 },
            bgcolor: "rgba(12, 18, 40, 0.82)",
            backdropFilter: "blur(6px)",
            borderColor: "rgba(243, 231, 191, 0.18)",
          }}
        >
          <Stack direction="row" spacing={3} sx={{ alignItems: "center", mb: 2 }}>
            <CrescentMoon />
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  lineHeight: 1.6,
                  display: "block",
                  color: MOONLIGHT,
                }}
              >
                Personal sandbox · off for the night
              </Typography>
              <Typography
                variant="h3"
                component="h1"
                sx={{ fontWeight: 400, fontSize: { xs: "2rem", sm: "2.75rem" }, lineHeight: 1.15 }}
              >
                {env ? (
                  <>
                    Sandbox <Code>{env}</Code> is turned off
                  </>
                ) : (
                  "This sandbox is turned off"
                )}
              </Typography>
            </Box>
          </Stack>

          <Typography variant="body1" sx={{ fontSize: "1.125rem" }}>
            Personal sandboxes shut down every night to save AWS costs. Your data is safe: nothing
            is deleted, the sandbox's services are only scaled to zero until you turn it back on.
          </Typography>

          <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
            Turn it on
          </Typography>
          <Typography variant="body1">From your ash checkout, run:</Typography>
          <CommandBlock command={`yarn encamp sandbox up --profile ${profile}`} />
          <Typography variant="body1">
            It takes about 1-3 minutes. When it finishes, click <strong>Check again</strong>. A
            sandbox turned back on stays on for the rest of that night.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            Or run <Code>{`yarn encamp sandbox open --profile ${profile}`}</Code>, which turns it on
            and opens it in your browser.
          </Typography>

          <Typography variant="h6" component="h2" sx={{ mt: 4 }}>
            Change the shutdown time or skip nights
          </Typography>
          <Typography variant="body1">
            The default is 7pm in your local time zone. Set your own time, or skip shutdowns for up
            to 14 days:
          </Typography>
          <CommandBlock command={`yarn encamp sandbox schedule --profile ${profile}`} />

          <Alert
            severity="info"
            variant="outlined"
            sx={{
              mt: 3,
              color: "text.primary",
              borderColor: "rgba(243, 231, 191, 0.3)",
              "& .MuiAlert-icon": { color: MOONLIGHT },
            }}
          >
            These commands assume your AWS profile is named <Code>{profile}</Code>. If yours has a
            different name, use that name for <Code>--profile</Code> and add{" "}
            <Code>{`--env ${profile}`}</Code>.
          </Alert>

          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Refresh />}
              loading={checking}
              loadingPosition="start"
              onClick={checkAgain}
            >
              {checking ? "Checking…" : "Check again"}
            </Button>
          </Box>

          {error !== undefined && (
            <>
              <Divider sx={{ my: 3 }} />
              <Button
                variant="text"
                size="small"
                aria-expanded={showDetails}
                onClick={() => setShowDetails((shown) => !shown)}
              >
                {showDetails ? "Hide error details" : "Not a shutdown? Show error details"}
              </Button>
              <Collapse in={showDetails}>
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  The sandbox&apos;s API did not answer. If{" "}
                  <Code>{`yarn encamp sandbox status --profile ${profile}`}</Code> says it is up,
                  this is a real error:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    p: 2,
                    overflowX: "auto",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    fontFamily: OAK_MONOSPACE_FONT_FAMILY,
                    fontSize: "0.8rem",
                    color: "error.main",
                  }}
                >
                  {errorDetails(error)}
                </Box>
              </Collapse>
            </>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
